import { useTranslation } from "react-i18next";
import type { EntitlementDecision, SkillSummary, UpgradeContext } from "@tabbio/types";
import { useToast } from "../../hooks/useToast";
import { useUpgradeModal } from "../../providers/useUpgradeModal";
import { useEntitlement } from "../../providers/SubscriptionProvider.shared";
import { createUpgradeContext } from "../../lib/monetization-context";
import { isEntitlementDenied } from "../../lib/skills-api";
import { useSkillInstallationMutation } from "./use-skills-query";

/**
 * Shared install/uninstall action for SkillInstallSheet (native + web).
 *
 * Installing is free at every tier: `individual.skill_run` is a free-tier
 * feature in the shared catalog, so the preflight below normally allows.
 * The entitlement plumbing stays wired so that if the catalog ever re-gates
 * skills, this client routes to the upgrade screen instead of a raw 403
 * (the API remains the enforcement of record). Uninstalling is never gated.
 */
export function useSkillInstallAction({
  skill,
  onClose,
  openPaywall,
}: {
  skill: SkillSummary | null;
  onClose: () => void;
  /**
   * Native sheet owners route the paywall themselves (close, then open from
   * the sheet's onAfterClose). Omitted: close and open the paywall at once.
   */
  openPaywall?: (context: UpgradeContext) => void;
}) {
  const { t } = useTranslation("common");
  const toast = useToast();
  const mutation = useSkillInstallationMutation();
  const upgradeModal = useUpgradeModal();
  const installEntitlement = useEntitlement("individual.skill_run");

  const installBlocked =
    !installEntitlement.allowed && installEntitlement.reasonCode !== "loading";
  const installLocked = Boolean(skill && !skill.installed && installBlocked);

  const openInstallPaywall = (decision: EntitlementDecision) => {
    const context = createUpgradeContext({
      decision,
      placement: "skill_install",
      entryPoint: "skills.install_sheet",
    });
    // A native BottomSheet owner must close first and open the paywall from
    // its onAfterClose (pushing over a presented modal drops the transition
    // on iOS); the web dialog has no such constraint and opens at once.
    if (openPaywall) {
      openPaywall(context);
      return;
    }
    onClose();
    upgradeModal.open(context);
  };

  const run = async () => {
    if (!skill) return;
    const installing = !skill.installed;
    if (installing && installBlocked) {
      openInstallPaywall(installEntitlement);
      return;
    }
    try {
      await mutation.mutateAsync({
        skillId: skill.skillId,
        install: installing,
      });
      toast.success(
        installing
          ? t("skills.toast.installed")
          : t("skills.toast.uninstalled"),
      );
      onClose();
    } catch (error) {
      if (installing && isEntitlementDenied(error) && error.decision) {
        openInstallPaywall(error.decision);
        return;
      }
      toast.error(
        t("skills.installFailed"),
        error instanceof Error ? error.message : undefined,
      );
    }
  };

  return { run, isPending: mutation.isPending, installLocked };
}
